/**
 * Create Rx: wire <-> screen mapping.
 *
 * Every picker row carries a `save` object — the EXACT shape that row must take
 * in the prescription payload. Building the body is then just "collect the
 * ticked rows' `save`", which is what stops the document and the payload
 * drifting apart.
 *
 * Payload shapes are bridge's, field-for-field (its PrescriptionPanel.addNew /
 * updateDraft + checkPayload):
 *   chiefComplaints [{ label, value }]
 *   diagnosis       [{ label, value }]
 *   reports         [{ reportName, testConductedDate(ISO), value,
 *                      valueUOM:{ value, label } }]   // note UOM, not Uom
 *   investigations  [{ labTest:{ label(UPPER), value, labName }, instruction }]
 *   instructions    [{ instruction(UPPER) }]
 */

import { htmlToText } from "components/common/richText";
import { INSTRUCTION_OPTIONS } from "modules/patient/sections/medicines/data";
import { signatureFor } from "./doctorSignatures";

/** Server gender enum -> the string the Rx payload wants. */
export const genderCode = (g) => (g === 1 || g === "1" ? "1" : g === 2 || g === "2" ? "2" : "");
export const genderWord = (g) => (genderCode(g) === "1" ? "Female" : genderCode(g) === "2" ? "Male" : "");

/**
 * REFLD codes for the height unit (bridge's constants). Only the ft/in code is
 * ever COMPARED against — the payload passes whatever unit the record holds
 * straight through, including "" — but cm is kept named so deriveBmi's else
 * branch isn't a bare magic default.
 */
export const HEIGHT_UOM_CM = "REFLD250";
export const HEIGHT_UOM_FTIN = "REFLD249";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "5 Sep, 2025" for document/label copy; "" when unparseable. */
export const shortDate = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "";
  return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${d.getFullYear()}`;
};

/** An ISO instant, or null — the payload's date fields are ISO or absent. */
export const toIso = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d.toISOString();
};

/** ft/in heights ride on the wire as "ft/in" (bridge's format, e.g. "5/8"). */
export const ftOf = (h) => String(h || "").split("/")[0] || "";
export const inOf = (h) => String(h || "").split("/")[1] || "";

/**
 * The height as the DOCUMENT states it — the one rule the preview and the
 * generated PDF both follow (api-prescription-service TransformMetaPrescriptionPdf).
 *
 * The VALUE only — the unit rides in the label (see heightLabel), the way the
 * strip already prints "Weight (Kg)" over a bare number (Nittin, 2026-09-24).
 * A ft/in height is the exception: it is stored as "5/8", which is not a height
 * anybody writes, so it is spelled out and states its own units.
 *
 * The PDF used to print "5'8'' (172 cm)" for both while the preview printed the
 * raw value — one document cannot state a patient's height two ways.
 */
export const heightText = (height, uom) => {
  const h = String(height == null ? "" : height).trim();
  if (!h) return "";
  if (uom !== HEIGHT_UOM_FTIN && uom !== "REFLD228") return h;
  const ft = ftOf(h);
  const inches = inOf(h);
  if (!ft) return h;
  return inches ? `${ft}'${inches}"` : `${ft}'`;
};

/**
 * The metric's LABEL, which is where its unit belongs: "Height (cm)" over a
 * bare 172, exactly as "Weight (Kg)" sits over a bare 72.5.
 *
 * Centimetres is what the record holds — the anthropometric store is canonical
 * metric — so the unit can be stated once, in the heading, instead of being
 * repeated on every value. A ft/in height says its own units inside the value
 * ("5'8\""), so its label stays bare rather than claiming cm over a number that
 * is not in cm.
 */
export const heightLabel = (uom) =>
  uom === HEIGHT_UOM_FTIN || uom === "REFLD228" ? "Height" : "Height (cm)";
/**
 * BMI = kg / m², derived from whatever height unit is in play. ONE definition,
 * shared by the editable card, the document preview and the save body, so the
 * three can never disagree. "" when either input is missing — never a fake 0.
 */
export const deriveBmi = (v) => {
  const kg = parseFloat(v && v.weight);
  if (!kg || kg <= 0) return "";
  let metres;
  if (v.heightUom === HEIGHT_UOM_FTIN) {
    metres = ((parseFloat(ftOf(v.height)) || 0) * 12 + (parseFloat(inOf(v.height)) || 0)) * 0.0254;
  } else {
    metres = (parseFloat(v.height) || 0) / 100;
  }
  if (!metres || metres <= 0) return "";
  const bmi = kg / (metres * metres);
  return isFinite(bmi) && bmi > 0 ? String(Math.round(bmi * 10) / 10) : "";
};

/** A REFLD-coded value ({label,value}) rendered for display. */
const codeLabel = (c) => (c && (c.label || c.value)) || "";

/**
 * The payload's `value` for a complaint/diagnosis: the ICD service's own id
 * ("ICD1276"), which is what bridge sends and what the code pack calls `code`.
 *
 * NOT `icdCode` ("A04.5"). Both live on the pack — IcdAutocomplete stores
 * `{ code: item.value, label, icdCode: item.code }` — and phr was sending the
 * ICD-10 one, so its prescriptions carried a different code system from
 * bridge's for the same complaint. `icdCode` remains the display/dedup key.
 * Free-text rows have no pack at all, hence "".
 */
const catalogId = (pack) => (pack && (pack.code || pack.icdCode)) || "";

/* ------------------------------------------------------------------ *
 * prescriber -> the payload's doctorDetails
 * ------------------------------------------------------------------ */

const str = (v) => (v == null ? "" : String(v));

/**
 * `doctorDetails`, in bridge's exact wire shape (captured 2026-07-28).
 *
 * Bridge sends the doc-profile row essentially verbatim, so this mirrors it key
 * for key — INCLUDING the four the prescription model has no field for
 * (`orgCode`, `category`, `onboarded`, `countryCode`, `tenantId`), which Go
 * simply ignores. Two spellings look wrong and are not:
 *  - **`phoneno`**, lowercase — the model's tag is `phoneNo`, but Go's
 *    json.Unmarshal matches field names case-insensitively, and bridge has
 *    always sent the user service's spelling.
 *  - `profile_image` is snake_case, the only such key in the whole payload.
 * Nulls are passed through rather than coerced to 0/"": bridge sends
 * `createdAt: null`, and `null` into a non-pointer int64 leaves the Go zero
 * value without erroring.
 *
 * `doctorProfile` (from login) is the whole doc_profile row and is the primary
 * source; the patient's care-team copy is only a fallback. That order matters:
 * `careTeam` is an optional, omitempty field whose upstream fetch fails
 * silently, so spreading it first (what this used to do) meant a care-team
 * outage quietly stripped clinic/location/title/etc. off a published Rx.
 */
const DOCTOR_KEYS = [
  "createdAt", "updatedAt", "id", "phoneno", "name", "profile_image",
  "speciality", "clinicHospital", "location", "licenseNumber", "title",
  "description", "type", "orgCode", "category", "status", "initiatedBy",
  "code", "email", "onboarded", "countryCode", "tenantId",
];

export const toDoctorDetails = (doctorProfile, fromTeam, fallbackName) => {
  const dp = doctorProfile || {};
  const ct = fromTeam || {};
  const pick = (key) => {
    const v = dp[key] != null && dp[key] !== "" ? dp[key] : ct[key];
    return v === undefined ? null : v;
  };
  const out = DOCTOR_KEYS.reduce((o, k) => {
    o[k] = pick(k);
    return o;
  }, {});
  // `name` is a *string with `required`: an absent key 400s, "" passes — so
  // never ship a blank prescriber onto a signed document.
  out.name = str(pick("name") || fallbackName);
  return out;
};

/* ------------------------------------------------------------------ *
 * API -> picker groups
 * ------------------------------------------------------------------ */

/** The JWT payload phr holds, or null. Claims: uid / who / whoId / profileId. */
const tokenClaims = (userProfile) => {
  const jwt =
    userProfile && userProfile.accessToken && userProfile.accessToken.accessToken;
  if (!jwt || typeof jwt !== "string") return null;
  const part = jwt.split(".")[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(decodeURIComponent(escape(window.atob(b64))));
  } catch (e) {
    return null;
  }
};

/**
 * The platform auth id — the JWT's `uid` — as a NUMBER.
 *
 * This is what `createdBy`/`updatedBy` must carry: a stored prescription records
 * 1869 there, NOT employeeProfile.id. phr's profile may have no top-level `id`
 * at all on the MFA login path, so reading the token is the only reliable source.
 *
 * Not the v2 services' actor: those stamp the token's `whoId` (a profile-table
 * id) and only fall back to `uid` — see `myActorIds` below.
 */
export const myUid = (userProfile) => {
  const claims = tokenClaims(userProfile) || {};
  const n = parseInt(claims.uid, 10);
  return isNaN(n) || n === 0 ? null : n;
};

/**
 * WHO "I" AM, as the v2 services record it: the (who, whoId) PAIR.
 *
 * CC, Diagnosis, Medicine and Lab-investigation v2 all stamp an actor the same
 * way (each service's controller `extractActor`): `{who: jwt.who, whoId:
 * jwt.whoId}`, falling back to the token's `uid` only when whoId is 0. `whoId`
 * is the id from WHICHEVER profile table `who` names — doctor.ID for a doclogin
 * session, employee.ID for a bridge employee (api-user-service login) — so a
 * bare number means nothing on its own: doctor 845 and employee 845 are two
 * different people, and both write to the same patient records (bridge's
 * care-team staff use the same section endpoints).
 *
 * This used to be a who-less list of four ids from three id spaces (uid,
 * profile id, employeeProfile.id, doctorProfile.id). Only doctorProfile.id ever
 * matched a doclogin session's stamps; the rest could only COLLIDE, pre-ticking
 * another person's entry into a prescription this doctor signs. So "me" is now
 * exactly the pair the services derive from this session's token, and nothing
 * when the token won't decode — an unmatched row just stays unticked in
 * "Ongoing", which is recoverable where a wrong tick is not.
 *
 * Returned as `"who:whoId"` keys (a list, so callers pass it through unchanged).
 */
export const myActorIds = (userProfile) => {
  const claims = tokenClaims(userProfile) || {};
  const who = String(claims.who || "").trim().toLowerCase();
  const whoId = parseInt(claims.whoId, 10) || parseInt(claims.uid, 10);
  return who && whoId > 0 ? [`${who}:${whoId}`] : [];
};

const actorKey = (a) => {
  if (!a || !a.who) return "";
  const n = Number(a.whoId);
  return n > 0 ? `${String(a.who).trim().toLowerCase()}:${n}` : "";
};

/** Was `actor` this session? A `service:0` stamp (the AI transcript's fan-out) never is. */
const isMe = (actor, me) => {
  const k = actorKey(actor);
  return Boolean(k && me && me.indexOf(k) !== -1);
};

/** Does an RFC3339 stamp fall on one of this consultation's days (browser-local)? */
const onConsultDay = (iso, days) => {
  if (!iso) return false;
  const d = new Date(iso);
  return !isNaN(d.getTime()) && days.indexOf(d.toDateString()) !== -1;
};

const historyOf = (x) => (Array.isArray(x && x.statusHistory) ? x.statusHistory : []);

/**
 * A marker that changes whenever the record behind a row does — anyone's new
 * history entry, a correction, a mark-incorrect, a lab row's updatedAt. Parked
 * with the default ticks so a returning visit can tell a row that CHANGED (and
 * stopped being this doctor's) from one that merely stopped being "today" when
 * the clock passed midnight — see rxScratch's mergePicks.
 */
const rowStamp = (x) => {
  const hist = historyOf(x);
  const last = hist[hist.length - 1] || {};
  return [
    x.updatedAt || "",
    hist.length,
    last.recordedAt || "",
    (last.correction && last.correction.at) || "",
    last.markedIncorrectAt || "",
  ].join("|");
};

const timeOf = (iso) => {
  const t = iso ? new Date(iso).getTime() : NaN;
  return isNaN(t) ? 0 : t;
};

/**
 * The same, counting only THIS doctor's own changes: their history entries,
 * their corrections, a lab row they last updated. A default the doctor unticked
 * stays unticked until THEY change the row again — a colleague's save, a
 * transcript write or a mark-incorrect is not the doctor deciding otherwise.
 * See rxScratch's declinedDefaults.
 */
const myRowStamp = (x, me) => {
  let count = 0;
  let latest = 0;
  const seen = (iso) => {
    count += 1;
    latest = Math.max(latest, timeOf(iso));
  };
  historyOf(x).forEach((e) => {
    if (!e) return;
    if (isMe(e.reportedBy, me)) seen(e.recordedAt);
    if (e.correction && isMe(e.correction.by, me)) seen(e.correction.at);
  });
  if (x.updatedBy && isMe(x.updatedBy, me)) seen(x.updatedAt);
  return `${count}|${latest}`;
};

/**
 * Was this entry CREATED by the person writing the prescription?
 *
 * `addedBy {who, whoId}` is on every v2 model (CC, Diagnosis, Medicine, Lab
 * investigation), so that is the answer. The creating history entry is the
 * fallback for a row without one. ONE actor is judged, not any of several: a
 * later "add" event (a lab reading appended to an existing test) was matching
 * the old any-of lookup and passing someone else's row off as this doctor's.
 */
const recordedByMe = (x, me) => {
  if (!me || !me.length) return false;
  const creator = x.addedBy && x.addedBy.who ? x.addedBy : (historyOf(x)[0] || {}).reportedBy;
  return isMe(creator, me);
};

/**
 * The days that count as "this consultation".
 *
 * Primarily TODAY — the day the doctor is writing this — but the appointment's
 * own day counts too, since an appointment dated other than today is still this
 * consultation. Comparing against the appointment date ALONE was a real bug:
 * entries added today never matched an appointment dated any other day.
 */
const consultationDays = (consultationDate) => {
  const days = [new Date().toDateString()];
  if (consultationDate) {
    const d = new Date(consultationDate);
    if (!isNaN(d.getTime())) days.push(d.toDateString());
  }
  return days;
};

/** "Added in this consultation" = added today AND by me. Both, not either. */
const addedByMeToday = (entry, days, me) =>
  onConsultDay(entry.addedAt, days) && recordedByMe(entry, me);

/* ------------------------------------------------------------------ *
 * "Updated today" — a doctor's save on an entry this consultation did not add
 * ------------------------------------------------------------------ *
 *
 * Decision (Nittin, 2026-09-14): a row the doctor UPDATED in this consultation
 * is treated like one they added — ticked, in the top group ("Added or updated
 * in this consultation"), a medicine locked like a new one — but badged
 * "Updated today" so the two stay distinguishable. ANY save by the doctor
 * counts; an adherence log and marking history incorrect do not.
 *
 * None of the four row shapes has a per-row "updated by" except lab
 * investigations; the others are read from statusHistory, where every save
 * that leaves a row active appends an entry stamped with `reportedBy` and a
 * server-side `recordedAt`. Everything below fails CLOSED: another person's
 * later save, a `service:0` write, an entry marked incorrect, a migration row.
 *
 * Known miss, deliberately not guessed at: saves made through the AI
 * transcript's "Save & next" reach the services with no actor headers, so they
 * are stamped `{who:"service", whoId:0}` and never count as anyone's.
 */

/**
 * The entries of the episode the ACTIVE row is in — after the last entry that
 * ended one (a resolve, a delete). A save of mine from an EARLIER episode that
 * a colleague then closed and reopened is theirs to prescribe on, not mine.
 */
const currentEpisode = (hist, endsEpisode) => {
  for (let i = hist.length - 2; i >= 0; i -= 1) {
    if (hist[i] && endsEpisode(hist[i])) return hist.slice(i + 1);
  }
  return hist;
};

/** What an entry said WHEN IT WAS WRITTEN — a correction rewrites the entry in place. */
const asWritten = (e, key) =>
  e.correction && e.correction.previousSnapshot && e.correction.previousSnapshot[key] !== undefined
    ? e.correction.previousSnapshot[key]
    : e[key];

/**
 * CC v2 / Diagnosis v2, which share a history model.
 *
 * `live(e)` says whether an entry leaves the row active (CC: vas > 0,
 * Diagnosis: ongoing), `endsEpisode(e)` whether it closed one, judged on the
 * value as written.
 *
 *  - "reopened": my status write today is the tail and the entry before it
 *    closed an episode (resolve → reopen, or re-adding a resolved/deleted
 *    entry). addedAt stays old on those paths, but the row is NEW to this
 *    consultation — the medicine "restart" of these sections.
 *  - "updated": any live status write of mine today in the current episode
 *    (the Update sheet, including a re-save), or my correction of the live
 *    entry that left it live.
 *
 * Deliberately not counted: a resolve (the row moves to `past`, which is not
 * shown), a correction of an older entry (the row's current state is
 * untouched), mark-incorrect (no new entry, and any entry so marked).
 */
const historyChangeByMe = (x, days, me, live, endsEpisode) => {
  if (!me || !me.length) return null;
  const hist = historyOf(x);
  const mine = (e) =>
    Boolean(e) &&
    e.event === "status" &&
    !e.markedIncorrect &&
    !e.isFromMigration &&
    live(e) &&
    onConsultDay(e.recordedAt, days) &&
    isMe(e.reportedBy, me);

  const last = hist[hist.length - 1];
  const episode = currentEpisode(hist, endsEpisode);
  // The episode's FIRST entry is the reopen; a later save of mine (or anyone's)
  // on top of it does not make the row any less new to this consultation.
  if (episode.length < hist.length && mine(episode[0])) return "reopened";

  if (episode.some(mine)) return "updated";
  const correctedLive = Boolean(
    last &&
      last.event === "status" &&
      last.correction &&
      !last.toWhen &&
      !last.markedIncorrect &&
      live(last) &&
      onConsultDay(last.correction.at, days) &&
      isMe(last.correction.by, me)
  );
  return correctedLive ? "updated" : null;
};

const ccLive = (e) => Number(e.vas) > 0;
// CC flips `ongoing` off on a status write with vas 0 (service UpdateStatus).
const ccEndsEpisode = (e) =>
  e.event === "delete" || (e.event === "status" && Number(asWritten(e, "vas")) === 0);
// A live entry corrected to Resolved keeps the ROW active (CorrectHistory never
// touches d.ongoing), but it is not an update to prescribe on.
const dxLive = (e) => e.ongoing === true;
const dxEndsEpisode = (e) =>
  e.event === "delete" || (e.event === "status" && asWritten(e, "ongoing") === false);

const complaintChangeByMe = (c, days, me) => historyChangeByMe(c, days, me, ccLive, ccEndsEpisode);
const diagnosisChangeByMe = (d, days, me) => historyChangeByMe(d, days, me, dxLive, dxEndsEpisode);

/**
 * Added by me today — AND still in the episode I started. A colleague who then
 * resolved and reopened it (or deleted and re-added it) made the active row
 * theirs; addedAt/addedBy never move on those paths, so they cannot say so.
 */
const addedInThisEpisode = (x, days, me, endsEpisode) => {
  const hist = historyOf(x);
  const last = hist[hist.length - 1];
  // A row whose live entry has been marked incorrect is not one to start ticked.
  if (last && last.markedIncorrect) return false;
  return addedByMeToday(x, days, me) && currentEpisode(hist, endsEpisode).length === hist.length;
};

/**
 * Medicine v2: the TAIL of statusHistory, and only the tail.
 *
 * Unlike a complaint or diagnosis, what a medicine row prints — dose,
 * frequency, duration — is whatever the LAST save wrote. If a colleague saved
 * after me, ticking the row would put their regimen on my prescription, so a
 * later save by anyone else means "not mine".
 *
 *  - "restarted": the entry before mine ended the course (completed,
 *    discontinued, auto-completed) or DELETED it — re-adding a deleted mCode
 *    reuses the record (service case R), leaving addedAt at the original.
 *  - "updated": anything else — an Update-sheet save, including one that only
 *    moved the note or the date (any save counts).
 *
 * Adherence reviews append no entry, so they never count.
 */
const medicineChangeByMe = (m, days, me) => {
  if (!me || !me.length) return null;
  const hist = historyOf(m);
  if (hist.length < 2) return null; // a lone entry is the add
  const last = hist[hist.length - 1];
  const prev = hist[hist.length - 2] || {};
  if (last.event !== "status" || last.status !== "ongoing") return null;
  if (!onConsultDay(last.recordedAt, days) || !isMe(last.reportedBy, me)) return null;
  return prev.event === "delete" || prev.status === "completed" || prev.status === "discontinued"
    ? "restarted"
    : "updated";
};

/**
 * Lab investigation v2: `updatedBy` / `updatedAt`, which every write that moves
 * the row's printed value stamps (add, append, batch, update, a correction of
 * the last-written entry). Two cases still must not tick:
 *
 *  - the printed value has been MARKED INCORRECT — mark-incorrect leaves the
 *    top level and its stamps alone (correction.go), so the row still says
 *    "updated by me today" while showing a value flagged as wrong;
 *  - the save went BACKWARDS — digitising an old report appends in write
 *    order, so the row falls back to an older reading while a newer one sits
 *    in its history. That is a backfill, not this consultation's result.
 */
/**
 * The readings a lab row has actually held, in write order. An `update` edits
 * the current reading IN PLACE (and snapshots it), so it replaces the reading
 * before it rather than sitting beside it — otherwise correcting a reading's
 * date backwards would look like a newer reading still on record.
 */
const labReadings = (x) =>
  historyOf(x).reduce((out, e) => {
    if (!e || e.event === "delete") return out;
    if (e.event === "update" && out.length) out[out.length - 1] = e;
    else out.push(e);
    return out;
  }, []);

/**
 * A reading's tag — the capturing caller's qualifier for readings that share one
 * test but mean different things ("Morning systolic" / "Evening systolic",
 * "PP1"–"PP3"). They all live in ONE investigation; the top-level `test.tag` is
 * the tag of whichever reading was written last. "" for catalogue picks.
 */
const labTagOf = (test) => (test && typeof test.tag === "string" && test.tag.trim()) || "";

/**
 * Is the value the row prints a reading to prescribe on — neither flagged wrong
 * nor superseded? A newer reading supersedes it unless both are tagged as
 * DIFFERENT measures: an evening reading does not make the morning one the row
 * prints out of date. An untagged reading (every catalogue pick, every write
 * from this app) is the same measure as any tag, so backfilling an old untagged
 * report over a newer tagged reading still reads as superseded.
 */
const labPrintedIsCurrent = (x) => {
  const readings = labReadings(x);
  const last = readings[readings.length - 1];
  if (last && last.markedIncorrect) return false;
  const shown = new Date(x.testDate).getTime();
  const tag = labTagOf(x.test);
  return !readings.some((e) => {
    const other = labTagOf(e.test);
    const sameMeasure = !tag || !other || other === tag;
    return !e.markedIncorrect && sameMeasure && new Date(e.testDate).getTime() > shown;
  });
};

const labUpdatedByMeToday = (x, days, me) =>
  onConsultDay(x.updatedAt, days) && isMe(x.updatedBy, me) && labPrintedIsCurrent(x);

/**
 * The v2 services all answer `{ active: [], past: [] }`, and all the picker cards
 * group it the same way, so the split lives here once.
 *
 * `past` is DROPPED, not rendered (Nittin, 2026-08-17). A stopped medicine or a
 * resolved complaint is not something this consultation can prescribe against —
 * these rows were already force-unticked and unpickable, so the group was pure
 * clutter between the doctor and the rows that matter.
 *
 * This is why the cards no longer show "Stopped" (medicines) or "Resolved"
 * (complaints, diagnosis). Lab investigations is unaffected: it envelopes its rows
 * with `past: []` and relabels the ONGOING group "Earlier results", so nothing it
 * shows came from here.
 *
 * The top group holds what THIS consultation touched — rows added (`isNew`) and
 * rows updated (`isUpdated`) — hence "Added or updated in this consultation"
 * (Nittin, 2026-09-14). Each row's own badge says which of the two it is.
 */
export const FRESH_GROUP_LABEL = "Added or updated in this consultation";

/** Did this consultation add or update the row? Those start ticked. */
export const touchedThisConsultation = (r) => Boolean(r && (r.isNew || r.isUpdated));

const groupRows = (doc, toRow, ongoingLabel) => {
  const rows = ((doc && doc.active) || []).map(toRow);
  const fresh = rows.filter(touchedThisConsultation);
  const ongoing = rows.filter((r) => !touchedThisConsultation(r));
  const groups = [];
  if (fresh.length) groups.push({ label: FRESH_GROUP_LABEL, rows: fresh });
  if (ongoing.length) groups.push({ label: ongoingLabel || "Ongoing", rows: ongoing });
  return groups;
};

/**
 * Chief complaints from phr's CC v2 (`{ active: [], past: [] }`) — Nittin's
 * call: phr shows its own newer complaints data rather than the Rx seed.
 *
 * Two groups, per Figma 3031:9587:
 *  - "Added or updated in this consultation" — added TODAY *and* by the
 *    logged-in user (tagged NEW TODAY; a resolved or deleted complaint they
 *    reopened today counts too), or updated by them today (tagged UPDATED
 *    TODAY). It is a special case of ongoing, so these rows are ticked by
 *    default: they are what this consultation just recorded.
 *  - "Ongoing" — everything else still active, left UNTICKED for the doctor to
 *    choose from.
 */
export const complaintsToGroups = (cc, consultationDate, myIds) => {
  const days = consultationDays(consultationDate);
  const row = (c) => {
    const name = c.complaint || codeLabel(c.complaintCode);
    const icd = (c.complaintCode && c.complaintCode.icdCode) || "";
    /* THE BARE NAME, on the wire as it is on the screen (Nittin, 2026-09-24).
       The ICD-10 code used to LEAD this label, matching bridge's captured
       payload — so the generated PDF's Chief Complaint line read "J02.9 Sore
       throat" while the preview of that same document said "Sore throat". The
       preview is what the patient receives, so the code goes.
       Nothing is lost by dropping it: `value` below carries the code itself
       (catalogId falls back to icdCode), and the complaint record keeps it.
       Diagnosis was never prefixed here — see buildDocument, which prints the
       code on the diagnosis LINE in both documents. */
    // "Added in this consultation" = added today AND by me. Both, not either.
    const change = complaintChangeByMe(c, days, myIds);
    const isNew = addedInThisEpisode(c, days, myIds, ccEndsEpisode) || change === "reopened";
    return {
      id: `cc-${c.complaintId || name}`,
      label: name,
      icd,
      isNew,
      isUpdated: !isNew && change === "updated",
      stamp: rowStamp(c),
      myStamp: myRowStamp(c, myIds),
      /* THE RECORD THIS ROW WAS BUILT FROM, carried by reference.
         Read by nothing on desktop. The mobile Rx screen's row pencil hands it
         to the section's own update sheet (MOBILE.EDIT_FOR_PICKER), which is
         seeded from the complaint record and not from the row — the row keeps only
         what the card and the document need.
         It costs no copy and never reaches the wire: the payload is built from
         `save` alone (toSaveBody), and the sessionStorage scratch parks `picks`,
         which are ids. */
      record: c,
      // The Rx payload's complaint shape — {label, value} only. Bridge's
      // captured payload carries no `complaintName`, and the model
      // (CodeValuePair) has no field for one.
      save: { label: name, value: catalogId(c.complaintCode) },
    };
  };
  return groupRows(cc, row);
};

/* ------------------------------------------------------------------ *
 * Diagnosis v2 -> picker groups
 * ------------------------------------------------------------------ */

/**
 * Diagnoses from phr's OWN Diagnosis v2 (`{ active: [], past: [] }`).
 *
 * The service is deliberately shaped like CC v2 (same envelope, same
 * `addedBy {who,whoId}` / `addedAt`, same `statusHistory[].reportedBy`), so the
 * grouping rule is identical: added (or reopened) TODAY *and* by the logged-in
 * user, or updated by them today, lands in "Added or updated in this
 * consultation" pre-ticked; everything else active is "Ongoing", unticked.
 *
 * Note `diagnosisCode` is omitempty server-side — a free-text diagnosis has no
 * such key at all, hence the guards. Such a row still saves: the payload only
 * requires `label`.
 */
export const diagnosisToGroups = (dx, consultationDate, myIds) => {
  const days = consultationDays(consultationDate);
  const row = (d) => {
    // `diagnosis` is the display string; the code pack is the fallback for a
    // code-only row. codeLabel() can't be reused — diagnosisCode is
    // {code,label,icdCode}, not a REFLD {label,value} pair.
    const name =
      d.diagnosis || (d.diagnosisCode && (d.diagnosisCode.label || d.diagnosisCode.code)) || "";
    const icd = (d.diagnosisCode && d.diagnosisCode.icdCode) || "";
    const change = diagnosisChangeByMe(d, days, myIds);
    const isNew = addedInThisEpisode(d, days, myIds, dxEndsEpisode) || change === "reopened";
    return {
      id: `dx-${d.diagnosisId || name}`,
      label: name,
      icd,
      isNew,
      isUpdated: !isNew && change === "updated",
      stamp: rowStamp(d),
      myStamp: myRowStamp(d, myIds),
      /* THE RECORD THIS ROW WAS BUILT FROM, carried by reference.
         Read by nothing on desktop. The mobile Rx screen's row pencil hands it
         to the section's own update sheet (MOBILE.EDIT_FOR_PICKER), which is
         seeded from the diagnosis record and not from the row — the row keeps only
         what the card and the document need.
         It costs no copy and never reaches the wire: the payload is built from
         `save` alone (toSaveBody), and the sessionStorage scratch parks `picks`,
         which are ids. */
      record: d,
      // {label, value} only — same as complaints; bridge sends no `diagName`.
      save: { label: name, value: catalogId(d.diagnosisCode) },
    };
  };
  return groupRows(dx, row);
};

/* ------------------------------------------------------------------ *
 * Lab investigation v2 -> picker groups (the Rx payload's `reports`)
 * ------------------------------------------------------------------ */

/**
 * A result's unit as the payload's REFLD pair.
 *
 * Lab investigation v2 stores the unit as PLAIN TEXT ("g/dL"), while the Rx
 * wants `{label, value}`. The code is recovered by matching the label against
 * LAB_TEST_UOM, and left BLANK when nothing matches — a near-miss ("g/dl" vs
 * "gm%") would stamp the wrong REFLD onto a prescription the doctor signs, and
 * blank is recoverable where wrong is not.
 *
 * Always an object with a string label, never omitted: `CodeValuePair.Label` is
 * a `*string` with `validate:"required"`, so a missing `valueUOM` is a nil
 * pointer and 400s the WHOLE prescription, not just the row.
 */
const uomPair = (unit, tables) => {
  const label = String(unit == null ? "" : unit).trim();
  if (!label) return { value: "", label: "" };
  const rows = (tables && tables.LAB_TEST_UOM) || [];
  const hit = rows.find(
    (e) => e && String(e.label || "").trim().toLowerCase() === label.toLowerCase()
  );
  return { value: (hit && hit.value) || "", label };
};

/**
 * Lab investigations from phr's OWN v2 service — the Rx card labelled "Lab
 * investigations" (payload key `reports`, which is fixed by the API).
 *
 * Three things make this NOT a copy of the other mappers:
 *
 * 1. The envelope is FLAT (`{investigations: []}`), with no active/past axis —
 *    a lab result is never "resolved". It is enveloped for groupRows, whose
 *    second group is relabelled "Earlier results": "Ongoing" is meaningless for
 *    a measurement.
 * 2. The list is append-only across the patient's whole history, so it is
 *    collapsed to the LATEST entry per test (the same rule the patient section
 *    itself uses: key on `sltCode || name`, newest `testDate` wins). Without
 *    that, three years of quarterly panels bury the card.
 * 3. "Updated today" reads the row's own `updatedBy`/`updatedAt` (see
 *    `labUpdatedByMeToday`), judged on the row the collapse KEEPS: the card
 *    shows the latest result per test, so it is ticked only when that latest
 *    result is this doctor's save today. A save to an older sibling row is not
 *    what the card prints.
 *
 * Rows that can't produce BOTH a name and a parseable date are dropped here:
 * buildSaveBody filters on exactly those two, so letting them through would
 * show a ticked row and print it in the preview while the payload silently
 * omitted it.
 */
export const labInvestigationsToGroups = (li, consultationDate, myIds, tables) => {
  const days = consultationDays(consultationDate);

  const all = (li && li.investigations) || [];
  const usable = all.filter((x) => x && x.test && x.test.name && toIso(x.testDate));

  // Latest per test, newest first.
  const latest = {};
  usable.forEach((x) => {
    const key = x.test.sltCode || x.test.name;
    const prev = latest[key];
    if (!prev || new Date(x.testDate) > new Date(prev.testDate)) latest[key] = x;
  });
  const rows = Object.keys(latest)
    .map((k) => latest[k])
    .sort((a, b) => new Date(b.testDate) - new Date(a.testDate));

  const row = (x) => {
    const t = x.test || {};
    const value = x.value == null ? "" : String(x.value);
    // Added by me today — and still MINE: a colleague's later reading on the
    // same row replaced what I added, so the card would print theirs. The same
    // printed-value guard as an update: a reading marked incorrect, or one a
    // backfilled older report now hides, is not one to start ticked.
    const updater = x.updatedBy && x.updatedBy.who ? x.updatedBy : x.addedBy;
    const isNew =
      addedByMeToday(x, days, myIds) && isMe(updater, myIds) && labPrintedIsCurrent(x);
    const reading = [value, t.unit].filter(Boolean).join(" ");
    const when = shortDate(x.testDate);
    // The reading's tag qualifies the name everywhere it is printed — picker,
    // preview AND the saved report name the PDF prints — or "Glucose, Post
    // Prandial 130 mg/dL" cannot say which reading it was. Parentheses, not a
    // middot: the PDF line is plain text, and matches the preview byte for byte.
    const tag = labTagOf(t);
    const reportName = tag ? `${t.name} (${tag})` : t.name || "";
    return {
      id: `rp-${x.investigationId || t.name}`,
      // Carried so an unresolved unit can be looked up against the lab-test
      // synonym map afterwards — see `unresolvedUnitRows`.
      sltCode: t.sltCode || "",
      // The picker renders the label ONLY, so the reading has to live in it —
      // a bare "HbA1c" gives the doctor nothing to choose on.
      label: [reportName, reading, when].filter(Boolean).join(" · "),
      isNew,
      isUpdated: !isNew && labUpdatedByMeToday(x, days, myIds),
      stamp: rowStamp(x),
      myStamp: myRowStamp(x, myIds),
      // Byte-identical to the PDF's own line (PrescriptionTransformer:
      // name + " (" + date + ") " + value + " " + unit).
      docLabel: [reportName, when ? `(${when})` : "", reading].filter(Boolean).join(" "),
      save: {
        reportName,
        testConductedDate: toIso(x.testDate),
        // Verbatim: "positive" and "<0.1" are real results that the record's
        // numericValue cannot represent.
        value,
        valueUOM: uomPair(t.unit, tables),
      },
    };
  };

  return groupRows({ active: rows }, row, "Earlier results");
};

/**
 * The lab-investigation rows whose unit did not resolve to a REFLD code.
 *
 * `uomPair` only matches when the record's unit is spelled exactly as the
 * reference list spells it, so real units ("g/dL" against a table holding
 * "gm/dl") fall through to a label-only pair. Those rows — and only those — are
 * worth a lookup against the lab-test synonym map, which stores the REFLD code
 * per sub-test. Rows that already resolved cost nothing.
 */
export const unresolvedUnitRows = (groups) =>
  (groups || [])
    .reduce((all, g) => all.concat(g.rows || []), [])
    .filter((r) => r.sltCode && r.save && r.save.valueUOM && r.save.valueUOM.label && !r.save.valueUOM.value);

/**
 * Stamp REFLD codes recovered from the synonym map onto their rows.
 *
 * `byCode` is `{sltCode: {unit, unitRef}}`. A code is accepted ONLY when the
 * catalogue's unit LABEL agrees with the one on the record: the map says what
 * a test is normally measured in, not what this particular result was measured
 * in, and a result recorded in mg/dL must never be stamped with the g/dL code.
 * Disagreement leaves the row blank, which is what it already was.
 *
 * The label itself is never overwritten — it is the record's own text and it is
 * what prints on the prescription.
 */
export const applyUnitRefs = (groups, byCode) => {
  const same = (a, b) =>
    String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
  return (groups || []).map((g) => ({
    ...g,
    rows: (g.rows || []).map((r) => {
      const hit = r.sltCode && byCode[r.sltCode];
      if (!hit || !hit.unitRef || !r.save || !r.save.valueUOM) return r;
      if (r.save.valueUOM.value) return r;
      if (!same(hit.unit, r.save.valueUOM.label)) return r;
      return { ...r, save: { ...r.save, valueUOM: { ...r.save.valueUOM, value: hit.unitRef } } };
    }),
  }));
};

/* ------------------------------------------------------------------ *
 * Medicine v2 -> picker groups
 * ------------------------------------------------------------------ */

/**
 * phr's instruction keys -> the words a prescription prints.
 *
 * DERIVED from the medicines section's own option set, not copied from it. This
 * was a hand-maintained duplicate and it had gone stale by exactly one key:
 * `local` ("Local Application"), the ointment-family instruction, was missing,
 * so `known` came out "" and the INSTRUCTIONS cell was left EMPTY — in the
 * document preview and in the payload the PDF prints from, since both read this
 * one lookup. An ointment therefore printed with no instruction at all
 * (Nittin, 2026-09-12).
 *
 * The section is the right source: `instructions` on the v2 wire stores THESE
 * keys (data.js says so), and a key added there now reaches the prescription
 * without anyone remembering this file exists.
 */
const INSTRUCTION_LABELS = INSTRUCTION_OPTIONS.reduce((all, o) => {
  if (o && o.key) all[o.key] = o.label || "";
  return all;
}, {});

/**
 * The two MEDICINE_TIMEPERIOD codes Create Rx emits (api-md-service's
 * config.properties: CONTINUED_REFLD_CODE / OTHER_DURATION_REFLD_CODE).
 *
 * Nothing else is safe to synthesise: phr's medicine v2 has no time-period
 * concept, only "indefinitely" vs a day count. Sticking to these two also keeps
 * clear of api-prescription-service's `medicineDurationOnlyPeriods` rule
 * (REFLD191/192/193/195 reject a 0-0-0 frequency), which matters because an
 * interval-based phr regimen ("every 6h") has no morning/afternoon/evening.
 */
const TIME_PERIOD_CONTINUED = "REFLD196";
const TIME_PERIOD_OTHER = "REFLD408";

/**
 * Dose quantities ride on the wire as strings so the fractions round-trip:
 * "½" -> 0.5, "1½" -> 1.5, "2" -> 2. 0 when there is nothing to read.
 */
const doseNumber = (raw) => {
  const s = String(raw == null ? "" : raw).trim();
  if (!s) return 0;
  const half = s.indexOf("½") !== -1 ? 0.5 : 0;
  // LEADING numeric token only. Stripping every non-digit instead would splice separate
  // numbers together — "1 tab 500mg" became 1500, which prints as a frequency of 1500 on
  // the prescription and multiplies straight into the ordered quantity.
  const found = s.match(/\d+(\.\d+)?/);
  const whole = found ? parseFloat(found[0]) : NaN;
  return (isNaN(whole) ? 0 : whole) + half;
};

/**
 * Units taken per dose — what the payload's three frequency slots actually mean.
 *
 * The dose box is a free-text value + unit pair, so it holds EITHER a count of dosage
 * units ("2" tablets) or a strength/volume ("500" mg, "5" ml). The slots are counts, so a
 * strength must never be written into one: "500 mg" published as a frequency prints
 * "500-0-0" on the signed prescription and explodes the ordered quantity.
 *
 * A strength/volume unit therefore counts as ONE dose per take. Carrying the amount itself
 * needs a real dose amount+unit field on the Rx row, which the payload does not have.
 */
const STRENGTH_UNIT = /^(mg|mcg|ug|g|kg|ml|l|cc|iu|units?)\b/i;

const doseUnitsPerTake = (doseQuantity) => {
  const dq = doseQuantity || {};
  const n = doseNumber(dq.value);
  if (!n) return 0;
  if (STRENGTH_UNIT.test(String(dq.unit || "").trim())) return 1;
  return n;
};

/**
 * Doses per DAY implied by an interval / custom regimen: "6h" -> 4, "48h" -> 0.5,
 * "every 2 Days" -> 0.5. Returns 0 when the regimen is not interval-based.
 */
const UNIT_DAYS = { Hours: 1 / 24, Days: 1, Weeks: 7, Months: 30 };

const dosesPerDay = (freq) => {
  const hours = parseFloat(String(freq.interval || "").replace(/[^0-9.]/g, ""));
  if (!isNaN(hours) && hours > 0) return 24 / hours;
  const every = Number(freq.customValue) || 0;
  const unitDays = UNIT_DAYS[freq.customUnit];
  if (every > 0 && unitDays) return 1 / (every * unitDays);
  return 0;
};

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Which slots a whole daily dose count occupies, in the usual clinical convention:
 * 1 = morning (OD), 2 = morning + evening (BD), 3 = morning/afternoon/evening (TDS),
 * 4 = all four including bedtime (QID). Indices are [morning, afternoon, evening, night].
 */
const SLOT_PATTERN = {
  1: [0],
  2: [0, 2],
  3: [0, 1, 2],
  4: [0, 1, 2, 3],
};

/**
 * Spread a daily dose across the four payload slots so their SUM equals the true daily
 * total — that sum is exactly what api-prescription-service's prescribedDoses consumes
 * ((morning+afternoon+evening+night) x days).
 *
 * Four doses a day (every 6h) lands as a clean 1-1-1-1; a sub-daily regimen (every 48h,
 * alternate-day) rides as a fraction in a single slot.
 */
const spreadDailyDose = (qty, perDay) => {
  const total = round2(qty * perDay);
  const out = [0, 0, 0, 0];
  if (!(total > 0)) return out;

  const whole = Math.floor(total);
  const rest = round2(total - whole);
  if (whole <= 4) {
    const pattern = SLOT_PATTERN[whole] || [];
    pattern.forEach((i) => {
      out[i] = 1;
    });
    if (rest > 0) {
      const slot = pattern.length ? pattern[pattern.length - 1] : 0;
      out[slot] = round2(out[slot] + rest);
    }
    return out;
  }

  // More than four doses a day (every 4h): distribute evenly, earliest slots take the
  // remainder so the day front-loads rather than stacking onto bedtime.
  const base = Math.floor(total / 4);
  let remainder = round2(total - base * 4);
  for (let i = 0; i < 4; i += 1) {
    out[i] = base;
    if (remainder >= 1) {
      out[i] += 1;
      remainder = round2(remainder - 1);
    }
  }
  if (remainder > 0) out[0] = round2(out[0] + remainder);
  return out;
};

/**
 * Resolve a REFLD table entry both ways. Medicine v2 stores the catalog's
 * `formulation` verbatim, and api-medicine-service's own filter notes it may
 * hold either a REFLD code or a plain label — so try `value` first, then
 * `label`.
 *
 * An unresolvable REFLD code yields a BLANK label on purpose: inventing
 * "Tablet" would put a made-up dosage form on a signed prescription. The blank
 * is what `medicineGaps` below turns into a save-blocking message.
 */
const refPair = (list, raw) => {
  const key = String(raw == null ? "" : raw).trim();
  if (!key) return { label: "", value: "" };
  const rows = Array.isArray(list) ? list : [];
  const byValue = rows.find((e) => e && String(e.value) === key);
  if (byValue) return { label: byValue.label || "", value: byValue.value || "" };
  const byLabel = rows.find(
    (e) => e && String(e.label || "").toLowerCase() === key.toLowerCase()
  );
  if (byLabel) return { label: byLabel.label || "", value: byLabel.value || "" };
  // Not in the table: a REFLD code we can't name stays nameless; anything else
  // is already human-readable, so keep it as the label.
  return /^REFLD\d+$/i.test(key) ? { label: "", value: key } : { label: key, value: "" };
};

/** "07"-style parts of a date, or null. The payload spells them out. */
const dateParts = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return null;
  const pad = (n) => (n < 10 ? `0${n}` : String(n));
  return { day: pad(d.getDate()), month: pad(d.getMonth() + 1), year: d.getFullYear() };
};

/**
 * Ongoing medicines from phr's OWN Medicine v2 (`{ active: [], past: [] }`).
 *
 * `tables` is the reference-list map (ReferenceListService) — needed because
 * medicine v2 keeps only the formulation's REFLD *code* while the prescription
 * API validates on `formulation.label` and its PDF prints
 * `formulation.label + MEDICINE NAME`.
 *
 * Grouping matches complaints/diagnosis (added today + by me => ticked), with
 * ONE medicine-specific addition: re-adding an ended course reuses the existing
 * record and does NOT refresh addedBy/addedAt (api-medicine-v2-service, case
 * "reactivated"), so a course restarted in this consultation is detected from
 * the tail of statusHistory instead.
 */
export const medicinesToGroups = (md, consultationDate, myIds, tables) => {
  const days = consultationDays(consultationDate);
  const t = tables || {};

  const row = (m) => {
    const state = m.current || {};
    const code = m.medicineCode || {};
    // "restarted" is new to this consultation; "updated" gets its own badge.
    // "Added" holds only while the regimen on record is still MINE — the lone
    // add, or a later save of my own: a colleague's save after mine means the
    // row now prints their regimen.
    const change = medicineChangeByMe(m, days, myIds);
    const added =
      addedByMeToday(m, days, myIds) && (historyOf(m).length <= 1 || change !== null);
    const isNew = added || change === "restarted";
    // The catalog name is the prescribed name; `medicine` is the fallback for a
    // free-text entry.
    const name = code.medicineName || m.medicine || "";
    // The Rx `medicine.value` is the catalog mCode — NOT medicineCode.id.
    const mCode = code.mCode || "";

    const formulation = refPair(t.MEDICINE_FORMULATIONS, m.formulation || code.formulation);
    // phr stores its own key ("after"); the payload wants the REFLD pair. Match
    // on the human label, then take the REFERENCE LIST's spelling of it — the
    // table holds "AFTER MEAL", and that label is what the PDF prints, so using
    // our own title-case would render differently from bridge's prescriptions.
    const known = INSTRUCTION_LABELS[state.instructions] || "";
    const instrPair = known ? refPair(t.MEDICINE_INSTRUCTIONS, known) : { label: "", value: "" };
    const instructions = {
      label: instrPair.label || known,
      // Not validated server-side, so an unresolved code is survivable — the
      // label is what prints.
      value: instrPair.value || "",
    };
    const instructionLabel = instructions.label;

    const freq = state.doseFrequency || {};
    const times = Array.isArray(freq.timesOfDay) ? freq.timesOfDay : [];
    /* PER-SLOT DOSES. A medicine whose frequency was set from "Times of day"
       carries its amounts in `perSlotQuantities` ({Morning:{value,unit}, …})
       and leaves the scalar `doseQuantity` UNDEFINED — MedicineStatusForm.js
       :589-603 sends one shape or the other, never both.

       This mapper read only the scalar, so for every such medicine
       `doseUnitsPerTake` returned 0 and ALL FOUR slots published as 0. That is
       not a display bug: the signed prescription stored 0-0-0-0, the generated
       PDF printed an empty Dose qty. cell (new.html:580 has no fallback), the
       medication-tracking record written back lost the regimen entirely, and
       the "Order medicine" floater fell back to 1 dose/day
       (floaterService.go:252-258), under-ordering a BD or TDS course two- to
       three-fold. Custom/interval mode was unaffected: it sends the scalar and
       is separately rescued by spreadDailyDose. */
    const perSlot =
      state.perSlotQuantities && typeof state.perSlotQuantities === "object"
        ? state.perSlotQuantities
        : null;
    const hasPerSlot = !!perSlot && Object.keys(perSlot).length > 0;

    const qty = doseUnitsPerTake(state.doseQuantity);
    /* Each ticked slot takes its OWN amount; a slot left blank still counts as
       one take, because it IS ticked. */
    const at = (slot) => {
      if (times.indexOf(slot) === -1) return 0;
      if (!hasPerSlot) return qty;
      return doseUnitsPerTake(perSlot[slot]) || 1;
    };

    // The dose AMOUNT, carried verbatim. The frequency slots are counts of takes and cannot hold
    // it, so without this a 5 ml syrup and a 5 tablet dose are indistinguishable on the
    // prescription — and for a strength/volume the amount used to vanish entirely.
    const dq = state.doseQuantity || {};
    /* The wire carries ONE scalar dose (Prescription.go:23), so a per-slot
       regimen has to nominate one. The first ticked slot with an amount is it —
       and when the slots disagree nothing is lost, because the frequency column
       below spells each one out. */
    const perSlotAmount = (() => {
      if (!hasPerSlot) return null;
      for (let i = 0; i < times.length; i += 1) {
        const q = perSlot[times[i]] || {};
        const v = String(q.value == null ? "" : q.value).trim();
        if (v) return { value: v, unit: String(q.unit == null ? "" : q.unit).trim() };
      }
      return null;
    })();
    const doseAmount = String(dq.value == null ? "" : dq.value).trim()
      ? {
          value: String(dq.value).trim(),
          unit: String(dq.unit == null ? "" : dq.unit).trim(),
        }
      : perSlotAmount;
    // Four times of day, four payload slots. Night is its OWN slot (nightFrequency), not
    // folded into evening: folding printed a QID 1-1-1-1 regimen as 1-1-1 (a dose lost) or
    // 1-1-2 (a bedtime dose misread as a double evening dose) on the signed prescription.
    let morning = at("Morning");
    let afternoon = at("Afternoon");
    let evening = at("Evening");
    let night = at("Night");
    // An interval / custom regimen ("every 6h", "every 2 Days") carries no timesOfDay at
    // all, so it published as 0-0-0 — printed verbatim on the signed prescription and read
    // downstream as 1 dose/day. Spread the real daily dose across the three slots instead;
    // the exact wording still rides along in the duration column via `qualifier` below.
    if (!times.length) {
      const spread = spreadDailyDose(qty, dosesPerDay(freq));
      morning = spread[0];
      afternoon = spread[1];
      evening = spread[2];
      night = spread[3];
    }

    const durationDays = Number(state.durationDays) || 0;
    const endParts = dateParts(state.medEndDate);
    let timePeriod;
    let otherDuration = "";
    if (state.ongoingIndefinitely) {
      const cv = refPair(t.MEDICINE_TIMEPERIOD, TIME_PERIOD_CONTINUED);
      timePeriod = { label: cv.label || "Continued", value: TIME_PERIOD_CONTINUED };
    } else if (durationDays > 0 || endParts) {
      // The PDF only substitutes otherDuration when the label is literally
      // "Other" (api-prescription-service TransformMetaPrescriptionPdf), so the
      // label is hardcoded rather than looked up.
      timePeriod = { label: "Other", value: TIME_PERIOD_OTHER };
      otherDuration =
        durationDays > 0
          ? `For ${durationDays} day${durationDays === 1 ? "" : "s"}`
          : `Till ${shortDate(state.medEndDate)}`;
    } else {
      // Neither indefinite nor bounded — under-specified. Left blank so the
      // save guard names it rather than the server 400ing on it.
      timePeriod = { label: "", value: "" };
    }

    // Extra frequency qualifiers phr can express but the payload cannot; shown
    // in the document's duration column so the instruction isn't lost.
    /* Units lowercased on BOTH branches. The interval branch used to interpolate
       freq.interval verbatim ("6 Hours"), which only ever showed in a picker row;
       it now prints on the signed prescription, where "every 6 Hours" next to the
       custom branch's "every 2 days" reads as a typo. */
    const qualifier = freq.interval
      ? `every ${String(freq.interval).toLowerCase()}`
      : freq.customValue && freq.customUnit
      ? `every ${freq.customValue} ${String(freq.customUnit).toLowerCase()}`
      : "";
    const durationText = [otherDuration || timePeriod.label, qualifier]
      .filter(Boolean)
      .join(" · ");

    /* WIRE-SAFE FREQUENCY. `times` holds STORED SLOT TAGS, and "asNeeded" is a
       tag rather than a label (MedicineForm's AS_NEEDED) — so it must not be
       published. api-prescription-service's formatFrequency
       (PrescriptionTransformer.go:121-124) joins `timesOfDay` verbatim whenever
       the array is non-empty, which put the literal "asNeeded" in the FREQUENCY
       column of a SIGNED PDF. `slotLabel` fixed the same thing for the preview
       column and stopped there, which is why only the document read correctly.

       The wording travels in `frequencyQualifier` instead — the field this
       screen already uses for "every 6 hours" and the branch formatFrequency
       falls to next. Nothing else changes: `times` itself is untouched, so the
       slot counts and the preview column are exactly as before, and the v2
       tracking payload never reads timesOfDay at all
       (PrescriptionTransformer.go's TransformMedicineV2 omits it), so this
       cannot move a tracking record.

       AND THE SLOTS STAY 0-0-0-0, deliberately. An as-needed medicine has no
       scheduled dose, and the service says so itself: the
       `medicineDurationOnlyPeriods` comment
       (controller/prescriptionController.go:602-604) excludes as-needed from
       needing a frequency because it "already implies how often", and neither
       code this screen sends is in that set. Synthesising a spread to avoid the
       zeros — which is what the `!times.length` rescue above does for an
       interval regimen — would invent a schedule nobody prescribed, print it on
       a signed document, and pass it to tracking as real slots. The zeros were
       only ever harmful because they PRINTED; the qualifier is what stops that,
       since formatFrequency reaches the slot notation only when both the times
       and the qualifier are empty. */
    const AS_NEEDED_TAG = "asNeeded";
    const wireTimes = times.filter((slot) => slot !== AS_NEEDED_TAG);
    const wireQualifier = [qualifier, times.length !== wireTimes.length ? "as needed" : ""]
      .filter(Boolean)
      .join(", ");

    /* NOTE: the qualifier is NOT appended to otherDuration. It travels in its own
       `frequencyQualifier` field and prints in the Frequency column, which is where
       a frequency belongs — appending it to the duration text would have changed a
       stored field's content for every interval regimen, and it would still have
       been dropped for indefinite ones (the PDF only substitutes otherDuration when
       the time period is literally "Other"). The dedicated field has neither
       problem: otherDuration keeps exactly the value it always had. */

    /* ---- document-preview display fields (board 3031:9853) ----
       The prescription document draws FOUR columns — MEDICINE / FREQUENCY
       (DOSE QTY.) / INSTRUCTIONS / TIME PERIOD — and each wants a different shape
       from the ones above, which exist to mirror the PDF and the card
       sub-line. Kept as separate `doc*` keys rather than reshaping those:
       `label`, `frequency` (the 1-0-1 slots) and `duration` are what the
       generated PDF and the picker rows are built from, and the save payload
       must not move. */
    /* MUST mirror api-prescription-service transformer.formatFrequency, in the same
       order, because this pane is a preview of that document: times of day, else the
       interval/custom qualifier, else the slot notation.

       The slot fallback is not dead code here — it is what the PDF prints for any
       prescription that carries neither field, which is everything written before
       they existed and everything bridge/expert-dashboard write today. Showing it
       keeps the preview honest for those rows too.

       Slots are cross-checked against the Go formatter for
       0/0.25/0.5/0.75/1/1.5/2/3/10/1.1: identical strings. They diverge only for a
       dose typed to ~8+ decimal places, where Go's float32 shortest-repr
       ("0.33333334") differs from JS float64 ("0.3333333333") — not worth a float32
       formatter, and such a dose is already meaningless on a prescription. */
    /* Board 3031:9853 merges the dose INTO this column — "FREQUENCY (DOSE
       QTY.)" reads "Morning (1), Afternoon (2), Night (1)". That is the one
       place a per-slot regimen can be stated without loss, which is why the
       amounts are appended here rather than collapsed into the single scalar.
       "asNeeded" is the stored tag, not a label — Medicines.js:74 already maps
       it, and printing it raw put "asNeeded" on the signed PDF. */
    const slotLabel = (slot) => (slot === "asNeeded" ? "As needed" : slot);
    /* What goes in the parentheses.
       The board prints a bare count — "Morning (1)" — because its sample is a
       tablet, where the unit repeats the formulation already shown in the
       MEDICINE column ("1. Montek LC (Tablet)"). It is NOT a bare count in
       general: a syrup's dose is "5 ml" and a strength dose is "1 x 500 mg",
       and this column is now the only place either can be stated — the separate
       "Dose qty." column the board removed was where they used to live. So the
       unit is dropped only when it says nothing the formulation has not already
       said, and printed otherwise.

       THE JOIN ITSELF is the PDF's, byte for byte (api-prescription-service
       transformer.formatDoseAmount) — it used to live in a `docDose` field
       that fed the removed column, and it is a safety rule rather than
       formatting. `unit` is free text used two ways: as a plain unit
       ("5" + "ml") and as a STRENGTH ("1" + "500 mg" = one 500 mg tablet).
       Space-joining the second form prints "1 500 mg", which reads as 1500 mg —
       a ten-fold misreading on a signed prescription. When the unit already
       contains a digit the value is a multiplier, so join with "x" to keep the
       two numbers apart. */
    const joinDose = (value, unit) => {
      if (!value) return unit;
      if (!unit) return value;
      return /\d/.test(unit) ? `${value} x ${unit}` : `${value} ${unit}`;
    };
    /* "Tablet/s" -> "tablet", "Units" -> "unit". The picker writes its count
       units with a "/s" plural. */
    const bare = (u) => String(u || "").trim().toLowerCase().replace(/\/s$|s$/, "");
    /* A unit says nothing when it is the generic count ("Unit/s") or simply
       repeats the formulation already printed beside the name ("Tablet/s" on a
       Tablet). Every other unit is a measure — ml, mg, drops — and must survive,
       because this column is now the only place a dose can be stated. */
    const sayNothing = (unit) => {
      const u = bare(unit);
      if (!u) return true;
      if (u === "unit") return true;
      const f = bare(formulation.label);
      return !!f && u === f;
    };
    const amount = (q) => {
      if (!q) return "";
      const v = String(q.value == null ? "" : q.value).trim();
      if (!v) return "";
      const u = String(q.unit == null ? "" : q.unit).trim();
      return sayNothing(u) ? v : joinDose(v, u);
    };
    /* The scalar dose stands in for every ticked slot when no per-slot map was
       entered — the Custom path sets one amount for the whole regimen, and the
       board still shows it against each time of day. */
    const scalar = amount(doseAmount);
    const docFrequency = times.length
      ? times
          .map((slot) => {
            const a = hasPerSlot ? amount(perSlot[slot]) : scalar;
            return a ? slotLabel(slot) + " (" + a + ")" : slotLabel(slot);
          })
          .join(", ")
      : qualifier
      ? qualifier.charAt(0).toUpperCase() +
        qualifier.slice(1) +
        (scalar ? ` (${scalar})` : "")
      : /* The slot notation already IS the per-slot counts ("1-0-1"), so a
           parenthetical here would print the same number twice. */
        [morning, afternoon, evening].join("-") + (night > 0 ? `-${night}` : "");
    // Short form: "5 days" / "15 days" / "Continued" — not the PDF's
    // "For 15 days · every 6h" sentence.
    const docDuration = state.ongoingIndefinitely
      ? timePeriod.label || "Continued"
      : durationDays > 0
      ? `${durationDays} day${durationDays === 1 ? "" : "s"}`
      : state.medEndDate
      ? `Till ${shortDate(state.medEndDate)}`
      : "";

    /* "16 – 20 Sep 2025" under the duration (board 6360:4396/4397). Inclusive
       of both ends: a 5-day course started on the 16th runs through the 20th,
       which is what the board's own 5-days / 16–20 pair says. The month and the
       year are printed once when both ends share them and twice when they do
       not, so a course crossing a month or a year cannot be misread. */
    const docDates = (() => {
      if (state.ongoingIndefinitely) return "";
      const from = state.medStartDate ? new Date(state.medStartDate) : null;
      if (!from || isNaN(from.getTime())) return "";
      let to = null;
      if (state.medEndDate) {
        to = new Date(state.medEndDate);
        if (isNaN(to.getTime())) to = null;
      } else if (durationDays > 0) {
        to = new Date(from.getTime());
        to.setDate(to.getDate() + durationDays - 1);
      }
      if (!to) return "";
      const sameYear = from.getFullYear() === to.getFullYear();
      const sameMonth = sameYear && from.getMonth() === to.getMonth();
      const left = sameMonth
        ? String(from.getDate())
        : sameYear
        ? `${from.getDate()} ${MONTHS[from.getMonth()]}`
        : `${from.getDate()} ${MONTHS[from.getMonth()]} ${from.getFullYear()}`;
      return `${left} \u2013 ${to.getDate()} ${MONTHS[to.getMonth()]} ${to.getFullYear()}`;
    })();

    const start = dateParts(state.medStartDate);
    // Bridge's captured medicine row, key for key. Every duration/to field is
    // present and explicitly `null` when unset — bridge never omits them, and
    // `null` into Go's non-pointer int/string leaves the zero value.
    const save = {
      medicine: { label: name, value: mCode },
      formulation,
      instructions,
      timePeriod,
      otherDuration,
      /* THE THREE CELLS THE DOCUMENT PRINTS, in the document's own words.
         Everything above is the clinical record and stays exactly as it was;
         these carry what the preview RENDERS for this row, so the generated PDF
         cannot word the same prescription differently. That drift was real and
         it was the whole of Nittin's 2026-09-24 report: the per-slot doses never
         reached the wire at all (only the scalar `doseQuantity` did, so
         "Morning (1), Night (2)" printed as "Morning, Night"), and the date span
         under the duration had no field to ride in.

         Re-deriving these in Go instead would mean a second copy of
         `sayNothing`/`joinDose`, of the month-and-year elision, and of the
         duration wording — three formatting rules maintained twice in two
         languages, which is exactly how the two documents came apart. The PDF
         falls back to its own derivation when they are absent, so every
         prescription written before this (and everything bridge legacy and the
         expert dashboard write) renders byte for byte as it does today. */
      docFrequency,
      docDuration,
      docDates,
      morningFrequency: morning,
      afternoonFrequency: afternoon,
      eveningFrequency: evening,
      nightFrequency: night,
      doseQuantity: doseAmount,
      /* What the four slots above CANNOT say. They are counts, so ticking all four
         times of day and "every 6 hours" both publish as 1-1-1-1 — the second only
         because spreadDailyDose synthesises a spread rather than publishing 0-0-0.
         These two fields let the PDF print the regimen the doctor actually chose
         instead of the raw slots. Both are additive and optional: omitted (empty
         array / empty string) they change nothing, and the PDF falls back to the
         slot notation exactly as it renders bridge's prescriptions today. */
      timesOfDay: wireTimes,
      frequencyQualifier: wireQualifier,
      dayDuration: durationDays > 0 ? durationDays : null,
      daySinceWhen: start ? start.day : null,
      dayTo: null,
      monthDuration: null,
      monthSinceWhen: start ? start.month : null,
      monthTo: null,
      yearDuration: null,
      yearSinceWhen: start ? start.year : null,
      yearTo: null,
      // `entryId` and `ongoing` are deliberately ABSENT — both are v2-tracking
      // lifecycle keys. Bridge's payload has neither, and entryId in particular
      // is an api-md-service id: phr's medicineId there would point the Rx at a
      // record in a different service. Omitting `ongoing` (nil, not false)
      // leaves validateMedicines behaving exactly as `true` would.
      //
      // phr's medEndDate is expressed through `otherDuration` ("For 15 days"),
      // not dayTo/monthTo/yearTo — bridge has no to-date UI and always nulls
      // them, so a stored medicine carries no such values.
    };

    return {
      id: `md-${m.medicineId || name}`,
      // The raw v2 id, kept OUT of the id string: a free-text medicine has no
      // medicineId and falls back to its name, so `id` can't be sliced back
      // into one. This is what the DELETE endpoint is keyed on.
      medicineId: m.medicineId || null,
      // Figma prints the dosage form before the name ("Tablet Montek LC"), and
      // so does the generated PDF (formulation.label + MEDICINE NAME).
      label: [formulation.label, name].filter(Boolean).join(" "),
      // Matches the PDF: the fourth part appears only when a bedtime dose was prescribed,
      // so a three-slot regimen still reads as the familiar "1-0-1".
      frequency: night > 0
        ? `${morning}-${afternoon}-${evening}-${night}`
        : `${morning}-${afternoon}-${evening}`,
      instructions: instructionLabel,
      duration: durationText,
      // Document-preview columns — see the block above.
      docName: name,
      docForm: formulation.label || "",
      docFrequency,
      docDuration,
      docDates,
      isNew,
      isUpdated: !isNew && change === "updated",
      stamp: rowStamp(m),
      myStamp: myRowStamp(m, myIds),
      // What blocks a save, in the doctor's words. "" when the row is complete.
      /* THE RECORD THIS ROW WAS BUILT FROM, carried by reference.
         Read by nothing on desktop. The mobile Rx screen's row pencil hands it
         to the section's own update sheet (MOBILE.EDIT_FOR_PICKER), which is
         seeded from the medicine record and not from the row — the row keeps only
         what the card and the document need.
         It costs no copy and never reaches the wire: the payload is built from
         `save` alone (toSaveBody), and the sessionStorage scratch parks `picks`,
         which are ids. */
      record: m,
      gap: !formulation.label
        ? "no formulation on record"
        : !timePeriod.value
        ? "no duration on record"
        : "",
      save,
    };
  };

  return groupRows(md, row);
};

/**
 * Picked medicines the prescription API would reject, as
 * [{ label, gap }] — checked before saving so the doctor gets a message naming
 * the medicine and the missing field instead of a raw 400.
 */
export const medicineGaps = (pickedMedicines) =>
  (pickedMedicines || []).filter((r) => r && r.gap).map((r) => ({ label: r.label, gap: r.gap }));

/* ------------------------------------------------------------------ *
 * screen -> save body
 * ------------------------------------------------------------------ */

/**
 * The prescription payload, bridge's field set exactly.
 *
 * Always a CREATE with `status: true` — there is no draft, so no id/etag to
 * carry and no update path.
 *
 * Referrals ARE now sent: api-prescription-service gained an optional
 * `referrals` field (model.Referral) and prints them as their own PDF section,
 * so what the doctor writes on the card reaches the patient. The FOLLOW-UP card
 * is still not sent as its own field — it rides inside `instructions`, which is
 * where the document already puts it.
 */
/**
 * The follow-up date an interval lands on. Lives here rather than in rxData because
 * BOTH copies of the follow-up derive from it — the sentence the document prints and
 * the structured field the save body carries — and a second implementation would let
 * the printed date and the stored date drift apart.
 */
/**
 * The interval normalised to whole days. The card offers only "Days" and "Weeks", so
 * this is exact — not an approximation.
 *
 * ONE multiplier, used by both the date and the stored `days`, so the two can never
 * disagree about how long "2 weeks" is.
 */
export const followUpDays = (count, unit) => (Number(count) || 0) * (/week/i.test(unit) ? 7 : 1);

/**
 * The follow-up day, counted from `from` — the prescription's consultation date.
 *
 * Pass it. The default exists only for the card's live note, which has no consultation
 * date to hand. Everything that PERSISTS or PRINTS must pass the same value the save
 * body sends, because the stored `followUp.date` is a materialised
 * `consultationDate + days`: two independent `new Date()` calls would land on different
 * days whenever a save straddles midnight, and the stored date would then contradict
 * the two fields it is derived from.
 *
 * Counts from the prescription date, NOT the appointment date — an appointment dated
 * weeks ago would otherwise produce a follow-up already in the past.
 */
export const followUpDate = (count, unit, from) => {
  const base = from ? new Date(from) : new Date();
  if (isNaN(base.getTime())) return null;
  return new Date(base.getFullYear(), base.getMonth(), base.getDate() + followUpDays(count, unit));
};

/**
 * The structured follow-up for the save body, or null when none was scheduled.
 *
 * The date is serialised as UTC MIDNIGHT of the local calendar day. Sending the local
 * timestamp instead would store the previous day for any timezone east of Greenwich —
 * IST is +5:30, so a 31 Aug follow-up would persist as 30 Aug 18:30Z and land in the
 * wrong bucket in exactly the date-range queries this field exists to answer.
 */
const followUpBody = (followUp, consultationDate) => {
  if (!followUp || !followUp.scheduled) return null;
  // Derived from the consultationDate this same body carries, so
  // `date == consultationDate + days` holds by construction rather than by two
  // clocks happening to agree.
  const d = followUpDate(followUp.count, followUp.unit, consultationDate);
  if (!d || isNaN(d.getTime())) return null;
  return {
    count: Number(followUp.count) || 0,
    unit: followUp.unit || "",
    // The interval as a plain int, so a consumer can compare or aggregate without
    // interpreting the unit string. Derived from the same multiplier as the date.
    days: followUpDays(followUp.count, followUp.unit),
    date: new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString(),
  };
};

export const buildSaveBody = ({
  patientId,
  appointmentId,
  patient,
  fields,
  picked,
  instructions,
  referrals,
  followUp,
  doctor,
  consultationDate,
  employee,
}) => {
  const employeeName = (employee && employee.name) || "";
  const actorId = (employee && employee.uid) || (employee && employee.id) || 0;
  const body = {
    // The consultation this prescription belongs to. api-prescription-service gates the
    // "Order medicine" floater on a non-zero appointmentId, and uses it to fetch the
    // appointment for the plan id and the self-vs-family-member check — so omitting it
    // silently means the patient never gets the floater and can never order the medicines.
    // 0 when absent, which is exactly how the server reads "no appointment".
    appointmentId: parseInt(appointmentId, 10) || 0,
    patientDetails: {
      patientId: parseInt(patientId, 10),
      name: fields.fullName || "",
      age: parseInt(fields.age, 10) || 0,
      /* WHOLE MONTHS, and only under a year — `age` is an int of YEARS on the
         wire (api-prescription-service model/Prescription.go: `Age int`), so
         six months cannot be said there without saying six years. The PDF
         template prints this when age is 0. */
      ageMonths: parseInt(fields.ageMonths, 10) || 0,
      gender: genderCode(patient && patient.gender),
      height: fields.height == null ? "" : String(fields.height),
      // Passed through, NOT defaulted — bridge sends "" when the record has no
      // unit, and a REFLD250 next to a blank height is a unit for nothing.
      heightUom: fields.heightUom || "",
      weight: parseFloat(fields.weight) || 0,
      /* `validate:"required"` on the wire (api-prescription-service
         model/Prescription.go:81), and there is no longer an input for it: the
         Patient-details card stopped asking, so this is sourced from the
         anthropometric weigh-in alone. A patient with no reading has no date
         either, and the save would 400 on a field nobody can see — so it falls
         back to today. That is filler for a weight that does not exist, not a
         claim that one was taken: when there IS a weight, its real weigh-in
         date is what rides here. */
      dateOfWeightRecorded:
        toIso(fields.dateOfWeightRecorded) || new Date().toISOString(),
      bmi: parseFloat(deriveBmi(fields)) || 0,
    },
    chiefComplaints: (picked.complaints || []).map((r) => r.save).filter((s) => s && s.label),
    diagnosis: (picked.diagnosis || []).map((r) => r.save).filter((s) => s && s.label),
    reports: (picked.reports || [])
      .map((r) => r.save)
      .filter((s) => s && s.reportName && s.testConductedDate),
    medicines: (picked.medicines || []).map((r) => r.save).filter(Boolean),
    // The label goes UPPER on the wire and `labName` keeps the catalogue's own
    // casing — bridge does exactly this at save time, and the PDF re-uppercases
    // for display anyway. The `label` filter is load-bearing: Investigations
    // carries no validate tag, but the PDF transformer dereferences
    // *TestName.Label, so a label-less row would nil-panic the save server-side.
    investigations: (picked.investigations || [])
      .map((r) => r.save)
      .filter((s) => s && s.labTest && s.labTest.label)
      .map((s) => ({
        labTest: {
          label: String(s.labTest.label).toUpperCase(),
          value: s.labTest.value || "",
          labName: s.labTest.labName || s.labTest.label,
        },
        instruction: s.instruction || "",
      })),
    // Flatten BEFORE upper-casing, always. api-prescription-service renders the
    // PDF through html/template, so markup arrives at the patient as literal
    // "<B>…</B>"; and upper-casing markup first would corrupt it beyond even
    // that ("&nbsp;" -> "&NBSP;", hrefs mangled). phr's own card already
    // flattens on add — this is the guard for a draft bridge wrote, since bridge
    // shares these endpoints and does not.
    instructions: (instructions || [])
      .map((t) => htmlToText(t))
      .filter(Boolean)
      .map((t) => ({ instruction: t.toUpperCase() })),
    /* The follow-up, stored STRUCTURED as well as in the instruction line above.
       The line stays — it is what puts the follow-up on the printed document — but
       an uppercase English sentence buried in instructions[] cannot answer "whose
       follow-up is due next week" without parsing prose, and nothing downstream can
       tell it apart from any other advice. Both copies come from the same source, so
       they cannot disagree.

       Null unless actually SCHEDULED, matching the instruction line: `scheduled`
       starts false and Cancel clears it, so configuring an interval without clicking
       "Schedule follow-up" must not record a follow-up nobody asked for. */
    followUp: followUpBody(followUp, consultationDate),
    // Optional server-side, and the backend formats the printed sentence from
    // these parts — so they go up as the doctor wrote them, NOT pre-joined and
    // NOT upper-cased (a referral reason is prose, unlike the instruction
    // lines above). Flattened for the same html/template reason as those: the
    // PDF escapes markup, so any stray tag would print literally.
    // `docReason` is the document's wording of the reason; `reason` is the
    // card's. The document's is what the preview shows, so it is what prints.
    referrals: (referrals || [])
      .map((r) => ({
        specialty: htmlToText(r.specialty || ""),
        within: htmlToText(r.within || ""),
        reason: htmlToText(r.docReason || r.reason || ""),
        reportsShared: htmlToText(r.reportsShared || ""),
      }))
      .filter((r) => r.specialty || r.reason || r.reportsShared),
    consultationDate: toIso(consultationDate),
    /* The doctor's signature image, looked up by doc_profile id — the same
       hardcoded table the legacy Rx panels carry, since no backend stores one
       (see doctorSignatures.js). null for a doctor who is not in it, which is
       what phr sent for everyone before this: the PDF's own
       {{if .DoctorSignature}} then prints nothing rather than an empty box. */
    doctorSignature: signatureFor(doctor && doctor.id),
    doctorDetails: doctor || null,
    // Always published — the screen has no draft state (product, 2026-07-29).
    status: true,
    // ALWAYS false, deliberately. `true` makes api-prescription-service commit
    // the prescribed medicines into api-md-service's v2 tracking — bridge's
    // tracking store, which phr must not write to (Nittin, 2026-07-27). phr's
    // own medicine record is maintained from the Medicines section instead.
    isMedicineChanged: false,
    // The PLATFORM auth id (JWT uid) — what a stored prescription records and
    // what the v2 services use as the actor. employeeProfile.id is the fallback
    // only, for a session whose token won't decode.
    // Bridge sends all four on every save, create or update — not createdBy
    // only on create.
    createdByName: employeeName,
    updatedByName: employeeName,
    createdBy: actorId,
    updatedBy: actorId,
  };

  return body;
};
